import { useMemo, useState, useEffect } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader, useToast } from '@lms/ui';
import { SafeHtml } from '@/components/SafeHtml';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { contractAcceptSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';
import { authenticatedFetch } from '@/lib/authenticated-fetch';
import type { LegalPolicyConsentItem } from '@/features/legal-policies/types';
import { ContractSigningView } from './ContractSigningView';
import { stripContractSignatureSectionForSigningView } from '@/lib/contract-html-signing-view';
import { contractSigningPath } from '@/lib/student-contract-gate';
interface ContractRow {
  id: string;
  code: string;
  status: string;
  generatedAt: string;
  acceptedAt?: string | null;
  courseName: string;
  courseCode?: string;
  templateTitle: string;
}

interface ContractDetail extends ContractRow {
  renderedHtml: string;
}

const STATUS_LABELS: Record<string, string> = {
  pending_acceptance: 'Pendiente de firma',
  accepted: 'Aceptado',
};

type ContractFilter = 'all' | 'accepted' | 'pending' | 'archived';

const FILTERS: { value: ContractFilter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'accepted', label: 'Aceptados' },
  { value: 'pending', label: 'Pendientes de firma' },
  { value: 'archived', label: 'Archivados' },
];

function formatContractDate(iso: string): string {
  return new Date(iso).toLocaleString('es-CL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function contractDisplayTitle(row: ContractRow): string {
  return row.templateTitle?.trim() || 'Contrato';
}

function matchesContractFilter(row: ContractRow, filter: ContractFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'accepted') return row.status === 'accepted';
  if (filter === 'pending') return row.status === 'pending_acceptance';
  return row.status !== 'accepted' && row.status !== 'pending_acceptance';
}

function matchesContractSearch(row: ContractRow, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const haystack = [
    contractDisplayTitle(row),
    row.courseName,
    row.courseCode ?? '',
    row.code,
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(needle);
}

function contractViewHref(row: ContractRow): string {
  if (row.status === 'pending_acceptance') {
    return contractSigningPath(row.id);
  }
  return `/mis-contratos/${row.id}`;
}

export function MisContratosPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { canAccessAdmin } = usePermissions();
  const backTo = canAccessAdmin ? '/administracion?tab=general' : '/dashboard';
  const backLabel = canAccessAdmin ? 'Volver a Administración' : 'Volver al panel principal';
  const [searchParams] = useSearchParams();
  const firmarRequired = searchParams.get('firmar') === '1';
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<ContractFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [acceptedRead, setAcceptedRead] = useState(false);
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [acceptedLegalPolicyIds, setAcceptedLegalPolicyIds] = useState<Set<string>>(() => new Set());

  const { data: legalPoliciesData, isLoading: legalPoliciesLoading } = useQuery({
    queryKey: ['legal-policies', 'required-for-contract-signing', id],
    queryFn: () =>
      api<{ data: LegalPolicyConsentItem[]; acceptedIds: string[] }>(
        '/legal/policies/required-for-contract-signing',
      ),
    enabled: Boolean(id),
  });

  const legalPolicies = legalPoliciesData?.data ?? [];

  useEffect(() => {
    setAcceptedRead(false);
    setSignatureData(null);
    setAcceptedLegalPolicyIds(new Set());
  }, [id]);

  useEffect(() => {
    if (!legalPoliciesData?.acceptedIds?.length) return;
    setAcceptedLegalPolicyIds(new Set(legalPoliciesData.acceptedIds));
  }, [legalPoliciesData?.acceptedIds, id]);

  const allLegalAccepted = useMemo(() => {
    if (legalPolicies.length === 0) return true;
    return legalPolicies.every((policy) => acceptedLegalPolicyIds.has(policy.id));
  }, [legalPolicies, acceptedLegalPolicyIds]);

  function handleLegalPolicyToggle(policyId: string, accepted: boolean) {
    setAcceptedLegalPolicyIds((prev) => {
      const next = new Set(prev);
      if (accepted) next.add(policyId);
      else next.delete(policyId);
      return next;
    });
  }

  const { data: listData, isLoading: listLoading } = useQuery({
    queryKey: ['student', 'contracts'],
    queryFn: () => api<{ data: ContractRow[] }>('/student/contracts'),
    enabled: !id,
  });

  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ['student', 'contracts', id],
    queryFn: () => api<ContractDetail>(`/student/contracts/${id}`),
    enabled: Boolean(id),
  });

  const acceptMutation = useMutation({
    mutationFn: (payload: { signatureData: string; acceptedLegalPolicyIds?: string[] }) =>
      api(`/student/contracts/${id}/accept`, { method: 'POST', body: payload }),
    onSuccess: async () => {
      const welcomeName = user?.name?.trim().split(/\s+/)[0];
      toast(contractAcceptSuccessToast(welcomeName));
      await queryClient.invalidateQueries({ queryKey: ['student', 'contracts'] });
      await queryClient.invalidateQueries({ queryKey: ['student', 'contracts', id] });
      await queryClient.invalidateQueries({ queryKey: ['student', 'contracts', 'pending'] });
      await queryClient.invalidateQueries({ queryKey: ['legal-policies'] });

      const pending = await api<{ data: { id: string } | null }>('/student/contracts/pending');
      if (pending.data?.id) {
        navigate(`/mis-contratos/${pending.data.id}?firmar=1`, { replace: true });
        return;
      }

      if (firmarRequired) {
        navigate('/dashboard', { replace: true });
      }
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo confirmar el contrato'));
    },
  });

  async function downloadPdf(contractId: string, code: string) {
    const response = await authenticatedFetch(`/api/student/contracts/${contractId}/pdf`);
    if (!response.ok) return;
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `contrato-${code}.pdf`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  const rows = listData?.data ?? [];
  const pendingListContract = rows.find((row) => row.status === 'pending_acceptance');

  const filterCounts = useMemo(() => {
    return {
      all: rows.length,
      accepted: rows.filter((row) => row.status === 'accepted').length,
      pending: rows.filter((row) => row.status === 'pending_acceptance').length,
      archived: rows.filter(
        (row) => row.status !== 'accepted' && row.status !== 'pending_acceptance',
      ).length,
    };
  }, [rows]);

  const visibleRows = useMemo(() => {
    return rows.filter(
      (row) => matchesContractFilter(row, filter) && matchesContractSearch(row, searchQuery),
    );
  }, [rows, filter, searchQuery]);

  if (id) {
    const isPendingAcceptance = detail?.status === 'pending_acceptance';

    if (detailLoading || !detail || (isPendingAcceptance && legalPoliciesLoading)) {
      return (
        <div className="lms-site-admin-hub-container py-8 md:py-12">
          <div className="lms-student-contracts lms-site-admin-hub-page font-sans">
            <Loader label="Cargando contrato…" />
          </div>
        </div>
      );
    }

    if (isPendingAcceptance && !firmarRequired) {
      return <Navigate to={contractSigningPath(id)} replace />;
    }

    const canAccept = detail.status === 'pending_acceptance';
    const canSubmit =
      acceptedRead &&
      signatureData !== null &&
      allLegalAccepted &&
      !acceptMutation.isPending;
    const contractHtml = canAccept
      ? stripContractSignatureSectionForSigningView(detail.renderedHtml)
      : detail.renderedHtml;

    if (canAccept) {
      return (
        <ContractSigningView
          templateTitle={detail.templateTitle}
          contractHtml={contractHtml}
          firmarRequired={firmarRequired}
          legalPolicies={legalPolicies}
          acceptedLegalPolicyIds={acceptedLegalPolicyIds}
          onLegalPolicyToggle={handleLegalPolicyToggle}
          acceptedRead={acceptedRead}
          onAcceptedReadChange={setAcceptedRead}
          onSignatureChange={setSignatureData}
          isSubmitting={acceptMutation.isPending}
          canSubmit={canSubmit}
          onCancel={() => navigate('/mis-contratos')}
          onAccept={() => {
            if (!signatureData) return;
            const pendingIds = legalPolicies.map((policy) => policy.id);
            acceptMutation.mutate({
              signatureData,
              acceptedLegalPolicyIds: pendingIds.filter((policyId) => acceptedLegalPolicyIds.has(policyId)),
            });
          }}
        />
      );
    }

    return (
      <div className="lms-site-admin-hub-container py-8 md:py-12">
        <div className="lms-student-contracts lms-site-admin-hub-page font-sans">
        <div className="mb-8 flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div className="flex items-start gap-4">
            <HubBackLink to="/mis-contratos" label="Volver a mis contratos" />
            <div>
              <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">{detail.templateTitle}</h1>
              <p className="mt-1 text-dash-on-surface-variant">
                {detail.courseName} · {detail.code}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-dash-primary px-4 py-2 text-sm font-medium text-dash-primary transition-colors hover:bg-brand-50"
            onClick={() => void downloadPdf(detail.id, detail.code)}
          >
            <span className="material-symbols-outlined text-[1.25rem]" aria-hidden="true">
              download
            </span>
            Descargar PDF
          </button>
        </div>

        <article className="contracts-print-area lms-credentials-glass-panel rounded-2xl p-8 shadow-sm">
          <SafeHtml html={contractHtml} className="prose max-w-none" />
        </article>

        <p className="mt-4 text-sm text-green-700">
          Contrato {STATUS_LABELS[detail.status] ?? 'registrado'}.
          {detail.acceptedAt ? ` Fecha: ${new Date(detail.acceptedAt).toLocaleString('es-ES')}.` : ''}
        </p>
        </div>
      </div>
    );
  }

  if (!id && pendingListContract) {
    return <Navigate to={contractSigningPath(pendingListContract.id)} replace />;
  }

  return (
    <div className="lms-student-contracts lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex items-start gap-4">
        <HubBackLink to={backTo} label={backLabel} />
        <div>
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Mis contratos</h1>
          <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
            Contratos de servicios asociados a tus matrículas.
          </p>
        </div>
      </div>

      {rows.length > 0 && (
        <div className="sc-toolbar">
          <div className="sc-filters" role="group" aria-label="Filtrar contratos">
            {FILTERS.map((chip) => (
              <button
                key={chip.value}
                type="button"
                className={`sc-filter${filter === chip.value ? ' sc-filter--active' : ''}`}
                onClick={() => setFilter(chip.value)}
              >
                {chip.label} ({filterCounts[chip.value]})
              </button>
            ))}
          </div>
          <div className="sc-search">
            <span className="sc-search__icon" aria-hidden="true">
              <span className="material-symbols-outlined text-[19px]">search</span>
            </span>
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="sc-search__input"
              placeholder="Buscar por contrato, curso o código..."
              aria-label="Buscar por contrato, curso o código"
            />
          </div>
        </div>
      )}

      {listLoading ? (
        <div className="sc-loading">
          <Loader label="Cargando contratos…" />
        </div>
      ) : rows.length === 0 ? (
        <div className="sc-empty">
          <span className="material-symbols-outlined sc-empty__icon" aria-hidden="true">
            description
          </span>
          <h2 className="sc-empty__title">Sin contratos</h2>
          <p className="sc-empty__text">No tienes contratos generados todavía.</p>
        </div>
      ) : visibleRows.length === 0 ? (
        <div className="sc-empty">
          <span className="material-symbols-outlined sc-empty__icon" aria-hidden="true">
            folder_open
          </span>
          <h2 className="sc-empty__title">No se encontraron contratos</h2>
          <p className="sc-empty__text">
            No hay contratos que coincidan con los filtros aplicados o tu término de búsqueda.
          </p>
        </div>
      ) : (
        <div className="sc-list">
          {visibleRows.map((row) => {
            const accepted = row.status === 'accepted';
            const statusLabel = STATUS_LABELS[row.status] ?? row.status;
            const enrollmentLabel = row.courseCode
              ? `${row.courseName} · ${row.courseCode}`
              : row.courseName;

            return (
              <article key={row.id} className="sc-card">
                <div className="sc-card__main">
                  <div className="sc-card__icon" aria-hidden="true">
                    <span className="material-symbols-outlined text-2xl">description</span>
                  </div>
                  <div>
                    <div className="sc-card__title-row">
                      <h2 className="sc-card__title">{contractDisplayTitle(row)}</h2>
                      <span className="sc-card__id">ID: #{row.code}</span>
                    </div>
                    <div className="sc-card__association">
                      <div className="sc-card__course">
                        <span className="material-symbols-outlined text-[17px] text-slate-400">
                          school
                        </span>
                        <span className="sc-card__course-name">{row.courseName}</span>
                        <span className="sc-card__sep" aria-hidden="true">
                          ·
                        </span>
                        <span
                          className={`sc-card__status ${accepted ? 'sc-card__status--accepted' : 'sc-card__status--pending'}`}
                        >
                          {statusLabel}
                        </span>
                      </div>
                      <span className="sc-card__enrollment">
                        Matrícula vinculada:{' '}
                        <strong>{enrollmentLabel}</strong>
                      </span>
                    </div>
                    <dl className="sc-card__meta">
                      <div>
                        <dt>Fecha de aceptación:</dt>
                        <dd>
                          {row.acceptedAt ? formatContractDate(row.acceptedAt) : 'Pendiente'}
                        </dd>
                      </div>
                      <div>
                        <dt>Tipo de documento:</dt>
                        <dd>{row.templateTitle}</dd>
                      </div>
                      <div>
                        <dt>Validez:</dt>
                        <dd className={accepted ? 'sc-card__valid--active' : undefined}>
                          {accepted ? 'Vigente' : 'Pendiente de firma'}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>
                <div className="sc-card__actions">
                  <Link to={contractViewHref(row)} className="sc-card__btn-primary">
                    <span>{accepted ? 'Ver contrato' : 'Firmar contrato'}</span>
                    <span className="material-symbols-outlined text-[18px]" aria-hidden="true">
                      visibility
                    </span>
                  </Link>
                  {accepted && (
                    <button
                      type="button"
                      title="Descargar PDF firmado"
                      className="sc-card__btn-secondary"
                      onClick={() => void downloadPdf(row.id, row.code)}
                    >
                      <span className="material-symbols-outlined text-[18px] text-slate-500">
                        download
                      </span>
                      <span className="hidden sm:inline lg:inline">Descargar PDF</span>
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

